// ./src/hooks/useRsvpGroups.ts
//
// Loads an event's RSVP groups (active + disabled). `reload` refetches after a
// grouping change. The guest list maps guest.rsvpGroupId → group name from here.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { RsvpGroupService, RsvpGroup } from "@/utils/api";

export interface UseRsvpGroupsReturn {
  groups: RsvpGroup[];
  loading: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

export const useRsvpGroups = (eventId?: string | null): UseRsvpGroupsReturn => {
  const [groups, setGroups] = useState<RsvpGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setGroups([]);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await RsvpGroupService.list(eventId);

    if (myCall !== callIdRef.current) return;
    setLoading(false);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setGroups(result.data as RsvpGroup[]);
    } else {
      setError(result);
      setGroups([]);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  return { groups, loading, error, reload: load };
};
